/**
 * ``useKeepSelectedRowInView`` — shared scroll anchoring for the Platforms,
 * Detections, Alerts and Tracks list panels (REQ-UI-051; REQ-UI-182;
 * storyboard Flow 02 §"events timeline strip" + Flow Q §"Frame 2"; ADR-024 §5
 * selection contract).
 *
 * The lists are newest-first (Platforms: status-sorted) and update live, so
 * the content above the viewport changes under the operator many times a
 * second. Four behaviours hold:
 *
 *   1. **Selection change** — when a row becomes inspected (a list
 *      left-click, or a Map glyph/marker click — which sets both slots —
 *      while the panel is visible), centre it if it is not already fully
 *      visible. A row that is already fully visible (the direct-click case)
 *      is left where it is so the click itself never moves the list.
 *   2. **List update under a steady selection** — hold the selected row at
 *      its current viewport offset by re-positioning ``scrollTop`` for the
 *      height change above it. The offset is re-baselined on every
 *      ``scroll`` event, so the operator may scroll the held row anywhere
 *      and it stays *there* through later updates.
 *   3. **List update with no selection** — governed by the panel's
 *      **follow** toggle (REQ-UI-182; :mod:`@/multi-pane/list-follow-store`).
 *      *Following* pins the list to its top so the newest rows stay in view.
 *      *Not following* needs no scrolling at all since 2026-10-10: the panel
 *      **pauses its rows** (:func:`@/multi-pane/usePausedListRows`), so the
 *      list does not move and there is nothing to compensate for. The
 *      first-visible-row anchor is still measured and held, for the changes a
 *      paused list can still have — a track row's expansion opening or
 *      closing above the viewport. Switching the toggle **off** adopts
 *      whatever is in view at that moment as the anchor (never a baseline
 *      from before the list was pinned).
 *      Switching it **on** pins to the top at once **and keeps the list
 *      there** until the selection next changes: the resume is the operator's
 *      own action, and while a row was selected the hold in (1)/(2) otherwise
 *      swallowed it entirely and the toggle looked dead (operator report
 *      2026-10-10). A selection *change* after a resume hands the viewport
 *      back, because that change is a fresh reveal edge (REQ-UI-051) and
 *      following is the default state.
 *   4. **Panel becoming visible** — dockview renders panels into a
 *      persistent overlay container and toggles ``display:none`` for the
 *      inactive tab rather than unmounting (see
 *      ``dockview-core .../overlayRenderContainer`` — the focus container's
 *      ``display`` is driven by ``panel.api.isVisible``). A Map selection
 *      made while this panel's tab is deselected runs against a zero-size
 *      element (any scroll is a no-op), and *showing* the tab flips
 *      ``display`` back without changing any React dependency. We therefore
 *      re-centre on dockview's ``onDidVisibilityChange`` (PR #300 item 2).
 *
 * # Why every programmatic scroll is instant and absolute
 *
 * Until 2026-10-01 the hold was ``scrollTop += delta`` and the lists carried
 * ``scroll-behavior: smooth``, which that CSS **animates**: new rows pushed
 * the held row down at paint, the correction glided back over a few hundred
 * milliseconds, and because batches arrive faster than the glide finishes
 * each correction measured a mid-animation position — the held row lurched
 * (operator report 2026-10-01). Every scroll here is now
 * ``scrollTo({ behavior: "instant" })`` with an **absolute** target computed
 * from the row's content offset, which is correct whatever animation is in
 * flight. Flow Q's 160 ms ease is superseded for programmatic list
 * positioning (the lists no longer carry ``scroll-smooth``).
 *
 * # Why the hook never re-baselines after its own scroll
 *
 * Rows have fractional heights and browsers land ``scrollTop`` on whole
 * pixels, so every correction misses its target by a fraction. Re-measuring
 * the anchor after our own scroll would adopt that miss as the new baseline
 * and the misses would integrate — a held row crept ~0.5 px per update, 6 px
 * over six seconds, in the 2026-10-01 live probe. The anchor is therefore
 * re-baselined only by the operator's scrolling (and by a selection change);
 * the one ``scroll`` event the browser fires after a programmatic scroll is
 * recognised by its landing position and ignored. (Recognised by position,
 * not by a flag: a scroll that lands on the current pixel fires no event, and
 * a stale flag then swallowed the operator's next scroll — live, 2026-10-01.)
 *
 * The scroll listener is attached to whatever element ``listRef`` holds at
 * each commit, not once at mount: every list panel renders a loading state
 * first, so the ``<ul>`` usually appears a render or two after the hook does
 * (a listener attached at mount alone never saw the operator scroll — live,
 * 2026-10-01).
 *
 * The hook is DOM-driven: the held row is whichever carries
 * ``aria-selected="true"`` (for the Tracks panel that may be a nested member
 * row); the no-selection anchor is the first direct ``li[data-row-id]``
 * child whose bottom edge sits below the list's top edge. It no-ops when the
 * list is hidden (``clientHeight === 0``) or not yet laid out, so it is safe
 * to run on every render and from the deferred visibility callback.
 */
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

/**
 * The slice of dockview's ``DockviewPanelApi`` this hook needs. Declared
 * structurally so the panels stay decoupled from dockview (and trivially
 * mockable in tests); ``DockviewPanelApi`` is assignable to it. ``undefined``
 * when the panel is rendered outside a dockview host (tests / storybook),
 * in which case behaviour (4) does not apply.
 */
export interface PanelVisibilityApi {
  /** Whether the panel's tab is currently the active (visible) one. */
  readonly isVisible: boolean;
  /** Subscribe to visibility transitions; returns a disposable. */
  onDidVisibilityChange(listener: (event: { readonly isVisible: boolean }) => void): {
    dispose(): void;
  };
}

/** Per-call options. */
export interface KeepSelectedRowOptions {
  /**
   * Whether the panel follows live updates (REQ-UI-182). Governs rule (3)
   * above, and a flip from ``false`` to ``true`` pins the list to its top
   * even with a row selected. Defaults to ``true``; the panels pass their
   * :func:`useListFollow` flag.
   */
  readonly follow?: boolean;
  /**
   * Animation-frame source for the scroll verification below. Defaults to
   * ``globalThis.requestAnimationFrame``; ``requestFrame: null`` disables the
   * verification entirely. Tests pass
   * :func:`test/manual-frame.createManualFrame`'s ``timers`` so they fire the
   * frame they mean to instead of racing a real one — the same injection the
   * commit scheduler takes (:type:`CommitSchedulerTimers`).
   */
  readonly timers?: KeepSelectedRowTimers;
}

/** The animation-frame half of :type:`CommitSchedulerTimers`. */
export interface KeepSelectedRowTimers {
  /** Request one callback on the next frame; ``null`` disables verification. */
  readonly requestFrame?: ((callback: () => void) => number) | null;
  /** Cancel a frame request. Defaults to ``globalThis.cancelAnimationFrame``. */
  readonly cancelFrame?: (id: number) => void;
}

/**
 * A scroll this hook issued, held until the next frame so it can be checked.
 *
 * The panel host can undo a programmatic scroll **silently**: dockview renders
 * panels through a shared overlay container and *moves* the panel's DOM
 * subtree when a panel is activated, and moving a subtree resets every
 * descendant's ``scrollTop`` — emitting no ``scrollTo``, changing no React
 * dependency, and leaving every ancestor identity intact. The map→list reveal
 * (:func:`useRevealMapSelectionInList`) activates a panel on *every*
 * map-originated selection, so this landed one millisecond after the hook had
 * centred the selected row and left it off-screen (probed live 2026-10-09:
 * ``scrollTo`` landed at t=70 ms, the subtree moved at t=71 ms).
 *
 * Two things are therefore remembered, not one. The ``target`` so the scroll
 * can be re-applied; and the ``anchor`` the hook meant to hold, because the
 * reset emits a ``scroll`` event that does **not** land on our target, which
 * the scroll listener correctly reads as the operator's and re-baselines on —
 * after which every later :func:`restore` faithfully holds the row at its
 * clobbered, off-screen offset. Re-applying the scroll without the anchor
 * fixes the frame and leaves the list wrong from the next update onward.
 */
interface PendingScroll {
  readonly target: number;
  readonly anchor: Anchor | null;
}

/**
 * What the next list update must restore: a row and its viewport offset.
 * ``rowId === null`` means "the row carrying ``aria-selected``"; otherwise
 * the direct child whose ``data-row-id`` matches.
 */
interface Anchor {
  readonly rowId: string | null;
  readonly offset: number;
}

/** A row's top edge measured in the list's scroll-content coordinates. */
function contentTop(list: HTMLElement, row: HTMLElement): number {
  return row.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
}

/**
 * Scroll the list to ``top`` instantly. Returns whether a scroll was issued
 * (``false`` when the list is already there), so the caller can expect the
 * browser's follow-up ``scroll`` event and ignore it.
 */
function scrollListTo(list: HTMLElement, top: number): boolean {
  const target = Math.max(0, top);
  if (Math.abs(list.scrollTop - target) < 0.5) {
    return false;
  }
  // ``behavior: "instant"`` bypasses any ``scroll-behavior`` CSS; the
  // property fallback covers hosts without ``Element.scrollTo``.
  if (typeof list.scrollTo === "function") {
    list.scrollTo({ top: target, behavior: "instant" });
  } else {
    list.scrollTop = target;
  }
  return true;
}

/** The direct ``data-row-id`` children of the list, in document order. */
function anchorableRows(list: HTMLElement): HTMLElement[] {
  return Array.from(list.children).filter((child): child is HTMLElement =>
    child.hasAttribute("data-row-id"),
  );
}

/** The first anchorable row whose bottom edge is below the list's top edge. */
function firstVisibleRow(list: HTMLElement): HTMLElement | null {
  const listTop = list.getBoundingClientRect().top;
  return anchorableRows(list).find((row) => row.getBoundingClientRect().bottom > listTop) ?? null;
}

/**
 * Keep the list's reading position stable per the rules above.
 *
 * @param listRef - ref to the scrollable ``<ul>`` list container.
 * @param selectedId - the currently-inspected id for this panel's kind, or
 *   ``null`` when nothing of this kind is inspected.
 * @param listRevision - any value that changes when the list content
 *   changes (e.g. the sorted rows array). Drives the re-anchor as rows
 *   stream in.
 * @param panelApi - dockview panel api for the show-to-scroll behaviour;
 *   omit when not hosted in dockview.
 * @param options - :attr:`KeepSelectedRowOptions.follow`.
 */
export function useKeepSelectedRowInView(
  listRef: React.RefObject<HTMLUListElement | null>,
  selectedId: string | null,
  listRevision: unknown,
  panelApi?: PanelVisibilityApi,
  options?: KeepSelectedRowOptions,
): void {
  const follow = options?.follow ?? true;
  const anchorRef = useRef<Anchor | null>(null);
  // Where our last programmatic scroll was aimed, so the browser's follow-up
  // ``scroll`` event can be recognised (it lands there) and not re-baseline
  // the anchor to the rounded landing position. Keyed on the position, not a
  // flag: a scroll that lands on the current pixel fires no event at all, and
  // a stale flag would swallow the operator's next scroll instead.
  const ownScrollTargetRef = useRef<number | null>(null);
  // The selection seen on the previous commit, to distinguish a selection
  // *change* (centre) from a list *update* under a steady selection (hold).
  const prevSelectedRef = useRef<string | null>(null);
  // The follow flag seen on the previous commit: a flip to "off" re-baselines
  // on the current viewport instead of restoring an anchor from before the
  // list was pinned.
  const prevFollowRef = useRef<boolean>(follow);
  // Set by a flip to *following*, cleared by the next selection change. A
  // resume is the operator's own action — "show me the newest rows again" —
  // so for as long as it stands it outranks the selected-row hold, which
  // otherwise makes the toggle look dead: with a row still selected the list
  // stayed where it was and went on tracking that row (operator report
  // 2026-10-10, measured at scrollTop 3806 for the whole window). It is
  // cleared by a selection *change* rather than left standing, because that
  // change is a fresh reveal edge — a map→list reveal under REQ-UI-051 has to
  // be honoured while following, which is the default state.
  const followResumeRef = useRef<boolean>(false);
  // The list element the scroll listener is currently attached to.
  const listenerRef = useRef<{ el: HTMLElement; off: () => void } | null>(null);
  // A selection whose centring could not be performed yet: the list element
  // did not exist (every panel renders a loading state first), the list had no
  // height (a hidden dockview tab), or the row had not reached the list (a
  // map-selected track the panel's 10 s poll has not listed). Centring is an
  // EDGE — it fires on the commit where the selection changes — so without
  // this the edge was spent against an unpositionable list and nothing ever
  // brought the row into view: ``restore`` baselines where the row *is*,
  // including off-screen. Latched here and honoured on the first later commit
  // that can, which is also what makes a late-arriving row work at all.
  const pendingRevealRef = useRef<string | null>(null);
  // The scroll awaiting its verification frame, and that frame's handle.
  const verifyRef = useRef<PendingScroll | null>(null);
  const verifyFrameRef = useRef<number | null>(null);
  // The frame source, held in a ref so the callbacks below keep stable
  // identities: the panels build their options object inline, so
  // ``options.timers`` is a fresh value on every render and listing it as a
  // dependency would re-run the layout effect — and therefore ``restore()`` —
  // on every render.
  const timersRef = useRef(options?.timers);
  // Written in a layout effect rather than during render (React forbids a
  // render-time ref write). Declared BEFORE the positioning effect below, and
  // with no dependency array, so it runs first on every commit including the
  // mount — the mount-time centring already has the caller's frame source.
  useLayoutEffect(() => {
    timersRef.current = options?.timers;
  });

  /** Request one frame from the injected source, else the platform's. */
  const requestFrame = useCallback((callback: () => void): number | null => {
    const custom = timersRef.current?.requestFrame;
    if (custom === null) {
      return null; // Verification explicitly disabled.
    }
    if (custom !== undefined) {
      return custom(callback);
    }
    if (typeof globalThis.requestAnimationFrame !== "function") {
      return null; // No frames here (SSR / a bare jsdom): skip verification.
    }
    return globalThis.requestAnimationFrame(() => {
      callback();
    });
  }, []);

  /** Drop any pending verification, cancelling its frame. */
  const cancelVerify = useCallback((): void => {
    const handle = verifyFrameRef.current;
    verifyFrameRef.current = null;
    verifyRef.current = null;
    if (handle === null) {
      return;
    }
    const custom = timersRef.current?.cancelFrame;
    if (custom !== undefined) {
      custom(handle);
      return;
    }
    if (typeof globalThis.cancelAnimationFrame === "function") {
      globalThis.cancelAnimationFrame(handle);
    }
  }, []);

  // Re-baseline: the selected row's offset, else the first visible row's.
  const measure = useCallback((): void => {
    const list = listRef.current;
    if (list === null || list.clientHeight === 0) {
      return;
    }
    const listTop = list.getBoundingClientRect().top;
    const selected = list.querySelector<HTMLElement>('[aria-selected="true"]');
    if (selected !== null) {
      anchorRef.current = { rowId: null, offset: selected.getBoundingClientRect().top - listTop };
      return;
    }
    const first = firstVisibleRow(list);
    anchorRef.current =
      first === null
        ? null
        : {
            rowId: first.getAttribute("data-row-id"),
            offset: first.getBoundingClientRect().top - listTop,
          };
  }, [listRef]);

  // Scroll, and remember where it was aimed.
  const scrollOwn = useCallback(
    (list: HTMLElement, top: number): void => {
      if (!scrollListTo(list, top)) {
        return;
      }
      const target = Math.max(0, top);
      ownScrollTargetRef.current = target;
      // Arm one verification frame (see :type:`PendingScroll`). Replaces any
      // frame still pending: the newest scroll is the one worth defending.
      cancelVerify();
      verifyRef.current = { target, anchor: anchorRef.current };
      const handle = requestFrame(() => {
        verifyFrameRef.current = null;
        const pending = verifyRef.current;
        verifyRef.current = null;
        const current = listRef.current;
        if (pending === null || current === null || current.clientHeight === 0) {
          return;
        }
        // Within a pixel of where we aimed: the scroll held (browsers land on
        // whole pixels), so there is nothing to defend against. An early-out,
        // not a correctness guard — ``scrollListTo`` below is already a no-op
        // at the target. It is here so a frame that found nothing wrong never
        // writes to ``anchorRef``, which a commit in the intervening ~16 ms
        // may legitimately have re-baselined.
        if (Math.abs(current.scrollTop - pending.target) < 1) {
          return;
        }
        // The host moved us. Put back both halves, and do NOT arm another
        // frame: a list that genuinely cannot reach the target must not be
        // fought every frame forever. One re-apply, then the ordinary
        // scroll-listener path owns the position again.
        anchorRef.current = pending.anchor;
        if (scrollListTo(current, pending.target)) {
          ownScrollTargetRef.current = pending.target;
        }
      });
      verifyFrameRef.current = handle;
    },
    [cancelVerify, listRef, requestFrame],
  );

  // Selection change: centre the row if it is off-screen, then baseline on
  // the intended offset (not the rounded landing position).
  const enter = useCallback((): boolean => {
    const list = listRef.current;
    if (list === null || list.clientHeight === 0) {
      return false;
    }
    const row = list.querySelector<HTMLElement>('[aria-selected="true"]');
    if (row === null) {
      anchorRef.current = null;
      return false;
    }
    const listTop = list.getBoundingClientRect().top;
    const rect = row.getBoundingClientRect();
    const offset = rect.top - listTop;
    const fullyVisible = offset >= 0 && rect.bottom - listTop <= list.clientHeight;
    if (fullyVisible) {
      // Directly-clicked, already-visible row: leave it where it is.
      anchorRef.current = { rowId: null, offset };
      pendingRevealRef.current = null;
      return true;
    }
    // Off-screen (Map click, or the panel just shown): centre it. The anchor
    // is set BEFORE the scroll so ``scrollOwn`` can remember the offset it is
    // scrolling to defend — see :type:`PendingScroll`.
    const centre = Math.max(0, (list.clientHeight - rect.height) / 2);
    anchorRef.current = { rowId: null, offset: centre };
    scrollOwn(list, contentTop(list, row) - centre);
    pendingRevealRef.current = null;
    return true;
  }, [listRef, scrollOwn]);

  // List update: put the anchor back where it was. The anchor itself is kept
  // (never re-measured here) so rounding cannot integrate across updates.
  const restore = useCallback((): void => {
    const list = listRef.current;
    if (list === null || list.clientHeight === 0) {
      return;
    }
    const anchor = anchorRef.current;
    if (follow && followResumeRef.current) {
      // A standing resume: the newest rows, at the top, whatever is selected.
      scrollOwn(list, 0);
      return;
    }
    const selected = list.querySelector<HTMLElement>('[aria-selected="true"]');
    if (selected !== null) {
      if (anchor !== null && anchor.rowId === null) {
        scrollOwn(list, contentTop(list, selected) - anchor.offset);
      } else {
        // A selection the DOM shows but no baseline yet: take one now.
        measure();
      }
      return;
    }
    if (follow) {
      scrollOwn(list, 0);
      return;
    }
    if (anchor !== null && anchor.rowId !== null) {
      const row = anchorableRows(list).find((r) => r.getAttribute("data-row-id") === anchor.rowId);
      if (row !== undefined) {
        scrollOwn(list, contentTop(list, row) - anchor.offset);
        return;
      }
    }
    // No anchor, or the anchor row has left the list (pruned): leave the
    // viewport alone and baseline on what is visible now.
    measure();
  }, [listRef, follow, measure, scrollOwn]);

  // The operator's own scrolling re-baselines the anchor (never scrolls);
  // the one event the browser fires after our scroll is ignored. Attached to
  // whatever element the ref holds now — re-attached when that changes.
  const ensureListener = useCallback((): void => {
    const list = listRef.current;
    if (listenerRef.current?.el === list) {
      return;
    }
    listenerRef.current?.off();
    listenerRef.current = null;
    if (list === null) {
      return;
    }
    const onScroll = (): void => {
      const target = ownScrollTargetRef.current;
      if (target !== null) {
        ownScrollTargetRef.current = null;
        // Browsers land on whole pixels: within 1 px of our target is ours.
        if (Math.abs(list.scrollTop - target) < 1) {
          return;
        }
      }
      // The operator has taken the viewport: a reveal still waiting for its
      // row must not yank it back when the row finally lands.
      pendingRevealRef.current = null;
      measure();
    };
    list.addEventListener("scroll", onScroll, { passive: true });
    listenerRef.current = {
      el: list,
      off: () => {
        list.removeEventListener("scroll", onScroll);
      },
    };
  }, [listRef, measure]);

  // Selection change → centre; follow switched off → re-baseline; list
  // update (or follow switched on) → restore. useLayoutEffect so the
  // correction lands before paint.
  useLayoutEffect(() => {
    ensureListener();
    const selectionChanged = prevSelectedRef.current !== selectedId;
    prevSelectedRef.current = selectedId;
    const followChanged = prevFollowRef.current !== follow;
    prevFollowRef.current = follow;
    if (selectionChanged) {
      // A verification still pending belongs to the PREVIOUS selection, whose
      // target is meaningless now; ``enter`` arms a fresh one when it scrolls.
      cancelVerify();
      // The new selection owns the viewport again (see ``followResumeRef``).
      followResumeRef.current = false;
      if (selectedId === null) {
        // Belt-and-braces: correctness does not rest on this clear — the
        // retry below is guarded on the id, so a latch left behind by a
        // selection that has since changed or cleared can never fire. It is
        // here so "no selection" means "no latch" when reading the state.
        pendingRevealRef.current = null;
        anchorRef.current = null;
        measure();
      } else {
        // ``enter`` clears the latch when it succeeds; record the id when it
        // could not. One pending reveal at a time — a newer selection
        // replaces an older one rather than queueing behind it.
        pendingRevealRef.current = enter() ? null : selectedId;
      }
      return;
    }
    if (pendingRevealRef.current !== null && pendingRevealRef.current === selectedId) {
      // The reveal this selection asked for is still owed. Retry it now that
      // the list may have gained an element, a height, or the row.
      if (enter()) {
        return;
      }
    }
    if (followChanged) {
      if (follow) {
        // An explicit resume. It stands until the selection next changes, and
        // it drops a reveal that was still owed: the operator has just asked
        // for the top of the list, not for a row they selected earlier.
        followResumeRef.current = true;
        pendingRevealRef.current = null;
      } else {
        // Hold whatever the operator is looking at now. Nothing is scrolled:
        // a paused list does not move (:func:`@/multi-pane/usePausedListRows`),
        // and this baseline is for the changes that still can — an expansion
        // opening or closing above the viewport.
        measure();
        return;
      }
    }
    restore();
  }, [selectedId, listRevision, follow, cancelVerify, ensureListener, enter, restore, measure]);

  // Detach the listener and drop any pending verification on unmount.
  useEffect(() => {
    return () => {
      listenerRef.current?.off();
      listenerRef.current = null;
      cancelVerify();
    };
  }, [cancelVerify]);

  // Panel becoming visible (dockview tab shown): re-centre a selection made
  // while we were display:none. Defer two animation frames so dockview's
  // overlay-container resize rAF sizes the now-visible panel first.
  useEffect(() => {
    if (panelApi === undefined) {
      return;
    }
    const recentre = (): void => {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          enter();
        });
      });
    };
    if (panelApi.isVisible) {
      recentre();
    }
    const disposable = panelApi.onDidVisibilityChange((event) => {
      if (event.isVisible) {
        recentre();
      }
    });
    return () => {
      disposable.dispose();
    };
  }, [panelApi, enter]);
}
